import { CyclingPowerEventData } from "../CyclingPowerEventData";
import { ITrainer } from "../SensorManager";
import { BluetoothClient } from "./BluetoothClient";
import { BluetoothUtils } from "./BluetoothUtils";


export class TrainerClient extends BluetoothClient implements ITrainer {
    get name(): string {
        return this.nativeDevice.name ?? "<No Name>";
    }
    setTargetPower(power: number): void {
        // console.log("TrainerClient: setTargetPower " + power);
        this.chFMCP?.writeValueWithResponse(new Uint8Array([0x05, power & 0xFF, (power >> 8) & 0xFF]));
    }
    setTargetResistance(resistance: number): void {
        // console.log("TrainerClient: setTargetResistance " + resistance);
        this.chFMCP?.writeValueWithResponse(new Uint8Array([0x04, resistance & 0xFF]));
    }

    private chFMCP: BluetoothRemoteGATTCharacteristic | null = null;

    protected override async connected(server: BluetoothRemoteGATTServer): Promise<void> {
        // console.log('Getting Service cycling_power');
        const service = await server?.getPrimaryService("cycling_power");

        // console.log('Getting characteristic cycling_power_measurement');
        const chCPM = await service?.getCharacteristic("cycling_power_measurement");

        // console.log("Starting notifications...");
        await chCPM.startNotifications();

        // console.log("Notifications started");

        chCPM?.addEventListener('characteristicvaluechanged', (event: Event) => {
            //console.log(event);
            var data = (event.target as BluetoothRemoteGATTCharacteristic)?.value;
            //console.log(data);  
            if (data != null)
                this.onCPMValueChanged(data);
        });

        const serviceFM = await server?.getPrimaryService("fitness_machine");

        //Fitness Machine Feature	org.bluetooth.characteristic.fitness_machine_feature	0x2ACC	GSS
        const chFMF = await this.readCharacteristic(serviceFM, "fitness_machine_feature");
        if (chFMF)
        {
            // const value = await chFMF.readValue();
            // const fitnessMachineFeatures = value.getUint32(0, true);
            // const targetSettingsFeatures = value.getUint32(4, true);
            // console.log("  fitnessMachineFeatures: " + BluetoothUtils.ToBinaryString(fitnessMachineFeatures));
            // console.log("  targetSettingsFeatures: " + BluetoothUtils.ToBinaryString(targetSettingsFeatures));
        }

        //Fitness Machine Control Point	org.bluetooth.characteristic.fitness_machine_control_point	0x2AD9	GSS
        this.chFMCP = await this.readCharacteristic(serviceFM, "fitness_machine_control_point");
        this.chFMCP?.addEventListener('characteristicvaluechanged', (event: Event) => {
            //console.log(event);
            var data = (event.target as BluetoothRemoteGATTCharacteristic)?.value;
            //console.log(data);  
            if (data != null)
                console.log("Fitness Machine Control Point: " + BluetoothUtils.DataViewToString(data));
        });
        this.chFMCP?.startNotifications();

        try
        {
            this.chFMCP?.writeValueWithResponse(new Uint8Array([0x01, 0x01]));
            console.log("Reset trainer successful");
        }
        catch (Exception)
        {
            console.log("Reset trainer failed");
            console.log(Exception);
        }

        //Indoor Bike Data	org.bluetooth.characteristic.indoor_bike_data	0x2AD2	GSS
        const chIBD = await this.readCharacteristic(serviceFM, "indoor_bike_data");

        //Supported Resistance Level Range	org.bluetooth.characteristic.supported_resistance_level_range	0x2AD6	GSS
        const chSRLR = await this.readCharacteristic(serviceFM, "supported_resistance_level_range");

        //Supported Power Range	org.bluetooth.characteristic.supported_power_range	0x2AD8	GSS
        const chSPR = await this.readCharacteristic(serviceFM, "supported_power_range");

        //Fitness Machine Status	org.bluetooth.characteristic.fitness_machine_status	0x2ADA	GSS
        const chFMS = await this.readCharacteristic(serviceFM, "fitness_machine_status");
        chFMS?.addEventListener('characteristicvaluechanged', (event: Event) => {
            //console.log(event);
            var data = (event.target as BluetoothRemoteGATTCharacteristic)?.value;
            //console.log(data);  
            if (data != null)
                console.log("Fitness Machine Status: " + BluetoothUtils.DataViewToString(data));
        });
        chFMS?.startNotifications();

        // console.log("TrainerClient: connected");
    }

    previousWheelTime: number = 0;
    previousWheelRevs: number = 0;
    previousWheelDataAvailable: boolean = false;
    previousCranckTime: number = 0;
    previousCranckRevs: number = 0;
    framesSinceLastCranckDataChanged: number = -1;
    previousCranckRpm: number = 0;

    private async readCharacteristic(serviceFM: BluetoothRemoteGATTService, characteristic: string) : Promise<BluetoothRemoteGATTCharacteristic | null>
    {
        let ch: BluetoothRemoteGATTCharacteristic | null = null;
        let debugMessage = characteristic;
        try
        {
            ch = await serviceFM?.getCharacteristic(characteristic);
            if (ch) {
                debugMessage += " (";
                debugMessage += ch.properties.read ? "R" : "";
                debugMessage += ch.properties.write ? "W" : "";
                debugMessage += ch.properties.notify ? "N" : "";
                debugMessage += ch.properties.indicate ? "I" : "";
                debugMessage += ch.properties.broadcast ? "B" : "";
                debugMessage += ch.properties.reliableWrite ? ".RW." : "";
                debugMessage += ch.properties.writableAuxiliaries ? ".WA." : "";
                debugMessage += ch.properties.authenticatedSignedWrites ? ".ASW." : "";
                debugMessage += ch.properties.writeWithoutResponse ? "w" : "";
                debugMessage += ")";

                if (ch.properties.read)
                {
                    const value = await ch.readValue();
                    debugMessage += ": " + BluetoothUtils.DataViewToString(value);
                }
            }
            else
            {
                debugMessage += ": not found";
            }
        }
        catch (ex: any)
        {
            debugMessage += ": " + ex;
        }

        // console.log(debugMessage);
        return ch;
    }

    private onCPMValueChanged(data: DataView) {
        const now = new Date();
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const seconds = now.getSeconds();
        const milliseconds = now.getMilliseconds();

        const formattedTime = `${hours}:${minutes}:${seconds}.${milliseconds}`;
        //console.log(formattedTime + ": " + BluetoothUtils.DataViewToString(data));
        let flags = data.getUint16(0);
        let instantaneousPower = data.getInt16(2, true);
        let pedalPowerBalance = data.getUint8(4);
        let wheelRevs = data.getUint32(5, true);
        let wheelTime = data.getUint16(9, true) / 2048;
        let cranckRevs = data.getUint16(11, true);
        let cranckTime = data.getUint16(13, true) / 1024;

        let wheelRpm = 0;
        let cranckRpm = 0;

        if (this.previousWheelDataAvailable) {
            let time = this.previousWheelTime - wheelTime;
            let revs = this.previousWheelRevs - wheelRevs;
            wheelRpm = 2048 * 60 * revs / time;
        }

        this.previousWheelDataAvailable = true;
        this.previousWheelRevs = wheelRevs;
        this.previousWheelTime = wheelTime;

        if (cranckTime != this.previousCranckTime) {
            if (this.framesSinceLastCranckDataChanged >= 0) {
                let timeCorrection = cranckTime < this.previousCranckTime ? 65536 : 0;
                let revsCorrection = cranckRevs < this.previousCranckRevs ? 65536 : 0;
                let time = cranckTime + timeCorrection - this.previousCranckTime;
                let revs = cranckRevs + revsCorrection - this.previousCranckRevs;
                cranckRpm = time == 0 ? Number.POSITIVE_INFINITY : 60 * revs / time;
                this.previousCranckRpm = cranckRpm;
            }

            this.framesSinceLastCranckDataChanged = 0;
        }

        else {
            if (this.framesSinceLastCranckDataChanged < 2) {
                cranckRpm = this.previousCranckRpm;
            }

            this.framesSinceLastCranckDataChanged++;
        }

        this.previousCranckRevs = cranckRevs;
        this.previousCranckTime = cranckTime;

        //console.log(`${this.framesSinceLastCranckDataChanged} ${this.previousCranckRevs}, ${this.previousCranckTime}, ${cranckRevs}, ${cranckTime} -> ${cranckRpm}`);
        let eventData = new CyclingPowerEventData(new Date());
        eventData.Power = instantaneousPower;
        eventData.CrankRpm = Math.round(cranckRpm);
        eventData.WheelRpm = Math.round(wheelRpm);
        this.onCyclingPowerDataReceived(eventData);
    };

    public onCyclingPowerDataReceived: (data: CyclingPowerEventData) => void = () => { };
}
